// Auditor-session hooks for /api/v1/auditor/*
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auditorApiClient } from '@/api/auditorClient';
import { useAuditorAuthStore } from '@/store/auditor-auth.store';
import type { AuditorSession } from '@grc/shared';
import type { AuditorFrameworkDetail, AuditorControlSummary, AuditorControlDetail } from '@/api/types/auditor.types';

const KEYS = {
  me: ['auditor', 'me'],
  frameworks: ['auditor', 'frameworks'],
  framework: (id: string) => ['auditor', 'frameworks', id],
  controls: (frameworkId: string, filters?: object) => ['auditor', 'controls', frameworkId, filters],
  control: (id: string) => ['auditor', 'control', id],
  compliance: ['auditor', 'dashboard', 'compliance'],
};

export function useAuditorActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (activationToken: string) => {
      const { data } = await auditorApiClient.post('/activate', { activation_token: activationToken });
      return data.data as {
        sessionToken: string;
        alreadyActive: boolean;
        auditor: AuditorSession & { scopedFrameworkNames: string[] };
      };
    },
    onSuccess: (result) => {
      useAuditorAuthStore.getState().login(result.sessionToken, {
        tokenId: result.auditor.tokenId,
        tenantId: result.auditor.tenantId,
        auditorEmail: result.auditor.auditorEmail,
        auditorDisplayName: result.auditor.auditorDisplayName,
        auditFirmName: result.auditor.auditFirmName,
        scopedFrameworkIds: result.auditor.scopedFrameworkIds,
        expiresAt: result.auditor.expiresAt,
      });
      qc.invalidateQueries({ queryKey: ['auditor'] });
    },
  });
}

export function useAuditorLogout() {
  return useMutation({
    mutationFn: async () => {
      await auditorApiClient.post('/logout');
    },
    onSuccess: () => {
      useAuditorAuthStore.getState().logout();
    },
  });
}

export function useAuditorMe() {
  const { isAuditorAuthenticated } = useAuditorAuthStore();
  return useQuery({
    queryKey: KEYS.me,
    queryFn: async () => {
      const { data } = await auditorApiClient.get('/me');
      return data.data as AuditorSession;
    },
    enabled: isAuditorAuthenticated,
    staleTime: 5 * 60_000,
  });
}

export function useAuditorFrameworks() {
  const { isAuditorAuthenticated } = useAuditorAuthStore();
  return useQuery({
    queryKey: KEYS.frameworks,
    queryFn: async () => {
      const { data } = await auditorApiClient.get('/frameworks');
      return data.data as AuditorFrameworkDetail[];
    },
    enabled: isAuditorAuthenticated,
    staleTime: 60_000,
  });
}

export function useAuditorFramework(frameworkId: string) {
  const { isAuditorAuthenticated } = useAuditorAuthStore();
  return useQuery({
    queryKey: KEYS.framework(frameworkId),
    queryFn: async () => {
      const { data } = await auditorApiClient.get(`/frameworks/${frameworkId}`);
      return data.data as AuditorFrameworkDetail;
    },
    enabled: isAuditorAuthenticated && !!frameworkId,
  });
}

export function useAuditorControls(
  frameworkId: string,
  filters?: { status?: string; category?: string },
) {
  const { isAuditorAuthenticated } = useAuditorAuthStore();
  return useQuery({
    queryKey: KEYS.controls(frameworkId, filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.set('status', filters.status);
      if (filters?.category) params.set('category', filters.category);
      const { data } = await auditorApiClient.get(`/frameworks/${frameworkId}/controls?${params.toString()}`);
      return data as { data: AuditorControlSummary[]; meta: { total: number } };
    },
    enabled: isAuditorAuthenticated && !!frameworkId,
    staleTime: 30_000,
  });
}

export function useAuditorControlDetail(frameworkId: string, controlId: string) {
  const { isAuditorAuthenticated } = useAuditorAuthStore();
  return useQuery({
    queryKey: KEYS.control(controlId),
    queryFn: async () => {
      const { data } = await auditorApiClient.get(`/frameworks/${frameworkId}/controls/${controlId}`);
      return data.data as AuditorControlDetail;
    },
    enabled: isAuditorAuthenticated && !!controlId,
  });
}

export function useAuditorComplianceDashboard() {
  const { isAuditorAuthenticated } = useAuditorAuthStore();
  return useQuery({
    queryKey: KEYS.compliance,
    queryFn: async () => {
      const { data } = await auditorApiClient.get('/dashboards/compliance');
      return data.data as {
        totalFrameworks: number;
        averageReadinessPct: number | null;
        frameworks: AuditorFrameworkDetail[];
      };
    },
    enabled: isAuditorAuthenticated,
    staleTime: 60_000,
  });
}
